/**
 * Chunk layout for the persistent voxel grid (docs/large-volume-plan.md §1).
 *
 * A voxel key is the 27-bit Morton code from `src/voxelMath.ts` (x0 y0 z0 x1 y1 z1 ...).
 * Because Morton interleaves bits, the low 12 bits of a key are exactly the Morton code of the
 * cell's position inside its 16³ chunk, and the high 15 bits are the Morton code of the chunk
 * coordinates. Chunk addressing therefore falls out of the existing keys for free:
 *
 *   chunkId  = key >>> 12        // 15-bit Morton of (cx, cy, cz), each 0..31
 *   top      = chunkId >>> 9     // 64 entries
 *   leafSlot = chunkId & 511     // 512 chunk refs per leaf (= 128³ cells)
 *   local    = MORTON12_TO_LINEAR[key & 4095]   // (lz << 8) | (ly << 4) | lx
 *
 * Cells inside a chunk are stored in linear order (x fastest) so x-spans are contiguous for the
 * brush's `writeSpanX` and the mesher's halo copy; the cost is one 8 KB LUT lookup per `get`.
 */
import { parseKeyXYZ, posKeyXYZ, type Position } from '@/voxelMath'

export const CHUNK_SHIFT = 4
export const CHUNK_SIZE = 1 << CHUNK_SHIFT // 16
export const CHUNK_MASK = CHUNK_SIZE - 1
export const CHUNK_CELLS = CHUNK_SIZE * CHUNK_SIZE * CHUNK_SIZE // 4096
/** Bytes of one chunk's cell array (Uint16 per cell). */
export const CHUNK_BYTES = CHUNK_CELLS * 2
/** Chunk coordinates are 5 bits each (512 / 16). */
export const CHUNKS_PER_AXIS = 32
/** Total addressable chunk ids (15-bit Morton). */
export const CHUNK_ID_COUNT = 1 << 15
export const TOP_ENTRIES = 64
export const LEAF_ENTRIES = 512
/** Exclusive upper bound of the key domain (27-bit Morton; coordinates 0..511). */
export const KEY_LIMIT = 1 << 27
/** Bounds covering the whole key domain; used when a grid is built without document bounds. */
export const FULL_DOMAIN_BOUNDS: Readonly<Position> = Object.freeze({ x: 512, y: 512, z: 512 })

export const MORTON12_TO_LINEAR = new Uint16Array(CHUNK_CELLS)
export const LINEAR_TO_MORTON12 = new Uint16Array(CHUNK_CELLS)
for (let lz = 0; lz < CHUNK_SIZE; lz++) {
  for (let ly = 0; ly < CHUNK_SIZE; ly++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const morton = posKeyXYZ(lx, ly, lz)
      const linear = (lz << 8) | (ly << 4) | lx
      MORTON12_TO_LINEAR[morton] = linear
      LINEAR_TO_MORTON12[linear] = morton
    }
  }
}

export const chunkIdOfKey = (key: number): number => key >>> 12
export const topIndexOfChunk = (chunkId: number): number => chunkId >>> 9
export const leafSlotOfChunk = (chunkId: number): number => chunkId & 511
export const localOfKey = (key: number): number => MORTON12_TO_LINEAR[key & 4095]
export const keyOf = (chunkId: number, local: number): number => (chunkId << 12) | LINEAR_TO_MORTON12[local]
export const localIndex = (lx: number, ly: number, lz: number): number => (lz << 8) | (ly << 4) | lx
/** Chunk id of the chunk holding cell (x, y, z). */
export const chunkIdOfXYZ = (x: number, y: number, z: number): number =>
  posKeyXYZ(x >> CHUNK_SHIFT, y >> CHUNK_SHIFT, z >> CHUNK_SHIFT)
/** Chunk id from chunk coordinates (each 0..31). */
export const chunkIdOfChunkXYZ = (cx: number, cy: number, cz: number): number => posKeyXYZ(cx, cy, cz)

export function chunkOrigin(chunkId: number): Position {
  const [cx, cy, cz] = parseKeyXYZ(chunkId)
  return { x: cx << CHUNK_SHIFT, y: cy << CHUNK_SHIFT, z: cz << CHUNK_SHIFT }
}

/** Chunk coordinates (0..31 each) of a chunk id. */
export function chunkCoords(chunkId: number): [number, number, number] {
  return parseKeyXYZ(chunkId)
}

/** Is `key` a valid cell key in the 27-bit Morton domain? */
export const isValidKey = (key: number): boolean => (key >>> 0) === key && key < KEY_LIMIT

/**
 * Per-chunk clip classification for a bounds box:
 *   0 = chunk entirely outside bounds, 1 = partially inside, 2 = entirely inside.
 * Null means every chunk is inside (bounds cover the whole domain), so callers can skip clipping.
 * Tables are shared per distinct bounds.
 */
export type ChunkClipTable = Uint8Array
const clipTables = new Map<string, ChunkClipTable | null>()

export function chunkClipTable(bounds: Readonly<Position>): ChunkClipTable | null {
  const bx = clampBound(bounds.x), by = clampBound(bounds.y), bz = clampBound(bounds.z)
  const cacheKey = `${bx},${by},${bz}`
  const cached = clipTables.get(cacheKey)
  if (cached !== undefined) return cached
  let table: ChunkClipTable | null
  if (bx === 512 && by === 512 && bz === 512) {
    table = null
  } else {
    table = new Uint8Array(CHUNK_ID_COUNT)
    for (let cz = 0; cz < CHUNKS_PER_AXIS; cz++) {
      for (let cy = 0; cy < CHUNKS_PER_AXIS; cy++) {
        for (let cx = 0; cx < CHUNKS_PER_AXIS; cx++) {
          const x0 = cx << CHUNK_SHIFT, y0 = cy << CHUNK_SHIFT, z0 = cz << CHUNK_SHIFT
          if (x0 >= bx || y0 >= by || z0 >= bz) continue // 0: outside
          const inside = x0 + CHUNK_SIZE <= bx && y0 + CHUNK_SIZE <= by && z0 + CHUNK_SIZE <= bz
          table[chunkIdOfChunkXYZ(cx, cy, cz)] = inside ? 2 : 1
        }
      }
    }
  }
  clipTables.set(cacheKey, table)
  return table
}

function clampBound(v: number): number {
  if (!Number.isFinite(v)) return 512
  return Math.max(0, Math.min(512, Math.floor(v)))
}

/** Normalize caller-provided bounds to a frozen, integer, domain-clamped box. */
export function normalizeBounds(bounds?: Readonly<Position> | null): Readonly<Position> {
  if (!bounds) return FULL_DOMAIN_BOUNDS
  const x = clampBound(bounds.x), y = clampBound(bounds.y), z = clampBound(bounds.z)
  if (x === 512 && y === 512 && z === 512) return FULL_DOMAIN_BOUNDS
  return Object.freeze({ x, y, z })
}
